import type React from 'react';
import type { FunctionComponent, ReactElement, ReactNode } from 'react';

import classNames from 'classnames';
import { startOfMonth } from 'date-fns';

import { OBJECT_TYPE_COLLECTIONS } from '@iconik-io/iconik-api/src/types/object_types';
import type { SearchItemEntity } from '@iconik-io/shared-lib/src/redux/entities/normalized/search_item';
import { isEnterpriseSecuritySettingActive } from '@iconik-io/shared-lib/src/utils/is_enterprise_security_active';
import type { UserRoles } from '@iconik-io/shared-lib/src/utils/roles';
import { Icon } from '@iconik-io/uad';

import {
  DRM_DESCRIPTION,
  DRM_LABEL,
  ENTERPRISE_SECURITY_HEADING,
  FORENSIC_WATERMARK_DESCRIPTION,
  FORENSIC_WATERMARK_LABEL,
} from '../../constants/enterprise_security';
import { ICONS } from '../../icons/icon';
import { useAppSelector } from '../../store/react_redux_typed';
import Collapsible from '../../ui/collapsible/collapsible';
import CheckboxFieldWrapper from '../../ui/forms/field_wrappers/checkbox/checkbox';
import CheckboxGroupFieldWrapper from '../../ui/forms/field_wrappers/checkbox_group/checkbox_group';
import SimpleDateFieldWrapper from '../../ui/forms/field_wrappers/date/date_simple';
import PasswordGenerator from '../../ui/forms/field_wrappers/password_generator/password_generator';
import RadioButtonsWrapper from '../../ui/forms/field_wrappers/radio_buttons/radio_buttons';
import SelectMetadataViewFieldWrapper from '../../ui/forms/field_wrappers/select_metadata_view/select_metadata_view';
import TextFieldWrapper from '../../ui/forms/field_wrappers/text/text';
import TextArea from '../../ui/forms/field_wrappers/textarea/textarea';
import VersionSelector from '../../ui/version_selector/version_selector';
import type { DefaultShareOptions } from '../../utils/set_default_share_settings';
import type { SelectedVersions } from '../modals/share/types';
import TooltipTrigger from '../tooltip_trigger/tooltip_trigger';
import type { ShareSettingFieldProps } from './types';
import UploadSetting from './upload_setting';
import UploadSwitch from './upload_switch';
import { hasShareFieldRoles, hasShareMultiFieldRoles } from './utils';

import styles from './share_settings.module.css';

const versionTypeOptions = [
  {
    label: 'Latest only',
    value: 'latest',
  },
  {
    label: 'All',
    value: 'all',
  },
  {
    label: 'Specify',
    value: 'specify',
  },
];

type Props = {
  objectType: string;
  objectId?: string;
  allowViewVersionsHidden?: boolean;
  showAdvanced: boolean;
  isPanel?: boolean;
  onAdvancedClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  fieldProps: ShareSettingFieldProps;
  roles: UserRoles;
  hidePasswordField?: boolean;
  allowSetSearchUsersFromShare?: boolean;
  hasPassword?: boolean;
  hideMessageField?: boolean;
  hideAllowSettingApprovalStatusField?: boolean;
  shouldCreatePlaylistShare: boolean;
  selectedItems?: SearchItemEntity[];
  showVersionSelector?: boolean;
  selectedVersions?: SelectedVersions;
  onSetSelectedVersions?: (value: SelectedVersions) => void;
};

const fieldSetLegends: Record<string, string> = {
  edit: 'Modify',
  review: 'Review & Approve',
  view: 'View',
  watermark: 'Visual Watermark',
  enterpriseSecurity: ENTERPRISE_SECURITY_HEADING,
};
const ShareSettings: FunctionComponent<Props> = ({
  showAdvanced,
  onAdvancedClick,
  fieldProps,
  isPanel,
  allowViewVersionsHidden,
  roles,
  hidePasswordField,
  objectType,
  objectId,
  allowSetSearchUsersFromShare,
  hasPassword,
  hideMessageField,
  hideAllowSettingApprovalStatusField = false,
  shouldCreatePlaylistShare,
  selectedItems,
  showVersionSelector,
  selectedVersions,
  onSetSelectedVersions,
}) => {
  const systemSettings = useAppSelector((state) => state.settings.system);
  const advancedFields: ReactNode[] = [];
  const defaultSettings = useAppSelector(
    (state) => state.settings.merged.default_share_options,
  );
  // See https://github.com/iconik-io/iconik_assets/blob/master/src/iconik_assets/constants.py
  // With one exception: don't check can_delete_segments for allowing comments.

  const fieldSets: Record<string, ReactNode[]> = {
    download: [],
    edit: [],
    review: [],
    view: [],
    watermark: [],
    enterpriseSecurity: [],
  };

  const allowOptionsToDisplay: string[] = [];

  if (hasShareFieldRoles('allow_download', roles)) {
    allowOptionsToDisplay.push('allow_download');
  }
  if (hasShareFieldRoles('allow_download_proxy', roles)) {
    allowOptionsToDisplay.push('allow_download_proxy');
  }

  if (hasShareMultiFieldRoles(['allow_upload'], roles)) {
    allowOptionsToDisplay.push('allow_upload');
  }
  if (hasShareFieldRoles('allow_custom_actions', roles)) {
    fieldSets.edit.push(
      <CheckboxFieldWrapper
        {...fieldProps.allow_custom_actions}
        disabled={defaultSettings?.can_change_allow_custom_actions === false}
        name="share.allow_custom_actions"
        key="allow_custom_actions"
        label="Custom Actions"
      />,
    );
  }

  if (
    hasShareFieldRoles('allow_setting_approve_status', roles) &&
    objectType !== 'collections' &&
    !hideAllowSettingApprovalStatusField
  ) {
    fieldSets.review.push(
      <CheckboxFieldWrapper
        {...fieldProps.allow_setting_approve_status}
        labelWidth="35%"
        helpMessage="Allow users invited by email to approve assets"
        name="share.allow_setting_approve_status"
        label="Allow approvals on assets"
        key="allow_setting_approve_status"
      />,
    );
  }

  if (hasShareFieldRoles('allow_comments', roles)) {
    fieldSets.review.push(
      <CheckboxFieldWrapper
        {...fieldProps.allow_comments}
        disabled={defaultSettings?.can_change_allow_comments === false}
        name="share.allow_comments"
        key="allow_comments"
        label="Add comments"
      />,
    );
  }

  if (
    hasShareFieldRoles('show_existing_comments', roles) &&
    shouldCreatePlaylistShare
  ) {
    fieldSets.review.push(
      <CheckboxFieldWrapper
        {...fieldProps.show_existing_comments}
        disabled={defaultSettings?.can_change_show_existing_comments === false}
        name="share.show_existing_comments"
        key="show_existing_comments"
        label="Show existing comments"
      />,
    );
  }

  if (hasShareFieldRoles('allow_approving_comments', roles)) {
    fieldSets.review.push(
      <CheckboxFieldWrapper
        {...fieldProps.allow_approving_comments}
        disabled={
          defaultSettings?.can_change_allow_approving_comments === false
        }
        name="share.allow_approving_comments"
        key="allow_approving_comments"
        label={'Make comments completable'}
      />,
    );
  }

  if (
    hasShareFieldRoles('allow_view_versions', roles) &&
    !allowViewVersionsHidden
  ) {
    fieldSets.review.push(
      <CheckboxFieldWrapper
        {...fieldProps.allow_view_versions}
        disabled={defaultSettings?.can_change_allow_view_versions === false}
        name="share.allow_view_versions"
        key="allow_view_versions"
        label="View Versions"
      />,
    );
  }

  if (
    hasShareFieldRoles('allow_sync', roles) &&
    objectType !== OBJECT_TYPE_COLLECTIONS
  ) {
    const extendedSettings = defaultSettings as
      | DefaultShareOptions
      | null
      | undefined;
    fieldSets.review.push(
      <CheckboxFieldWrapper
        {...fieldProps.allow_sync}
        disabled={extendedSettings?.can_change_allow_sync === false}
        name="share.allow_sync"
        key="allow_sync"
        label="Allow sync"
      />,
    );
  }

  if (
    hasShareFieldRoles('allow_user_search_for_mentions', roles) &&
    allowSetSearchUsersFromShare
  ) {
    fieldSets.review.push(
      <CheckboxFieldWrapper
        {...fieldProps.allow_user_search_for_mentions}
        name="share.allow_user_search_for_mentions"
        key="allow_user_search_for_mentions"
        label="Show all users in mentions"
      />,
    );
  }

  if (hasShareFieldRoles('allow_view_transcriptions', roles)) {
    fieldSets.view.push(
      <CheckboxFieldWrapper
        {...fieldProps.allow_view_transcriptions}
        disabled={
          defaultSettings?.can_change_allow_view_transcriptions === false
        }
        name="share.allow_view_transcriptions"
        key="allow_view_transcriptions"
        label="Transcriptions"
      />,
    );
  }

  if (hasShareFieldRoles('show_watermark', roles)) {
    fieldSets.watermark.push(
      <CheckboxFieldWrapper
        {...fieldProps.show_watermark}
        disabled={defaultSettings?.can_change_show_watermark === false}
        name="share.show_watermark"
        key="show_watermark"
        label="Watermark"
      />,
    );
  }

  if (isEnterpriseSecuritySettingActive(systemSettings.watermark)) {
    fieldSets.enterpriseSecurity.push(
      <div className={styles.watermark_check_container}>
        <span className={styles.watermark_check_label}>
          {FORENSIC_WATERMARK_LABEL}
        </span>
        <div className={styles.watermark_check}>
          <Icon name="protected" className={styles.watermark_check_badge} />
          <span className={styles.watermark_check_help_message}>
            {FORENSIC_WATERMARK_DESCRIPTION}
          </span>
        </div>
      </div>,
    );
  }

  if (isEnterpriseSecuritySettingActive(systemSettings.drm)) {
    fieldSets.enterpriseSecurity.push(
      <div className={styles.watermark_check_container}>
        <span className={styles.watermark_check_label}>{DRM_LABEL}</span>
        <div className={styles.watermark_check}>
          <Icon name="protected" className={styles.watermark_check_badge} />
          <span className={styles.watermark_check_help_message}>
            {DRM_DESCRIPTION}
          </span>
        </div>
      </div>,
    );
  }

  Object.entries(fieldSets).forEach(([key, fields]) => {
    if (fields.length) {
      advancedFields.push(
        <fieldset key={key} className={styles.fieldset}>
          <legend className={styles.legend}>{fieldSetLegends[key]}</legend>
          {fields}
        </fieldset>,
      );
    }
  });

  const showMetadataViews =
    roles.canReadMetadataValues &&
    roles.canReadMetadataFields &&
    roles.canReadMetadataViews &&
    roles.canReadMetadataCategories;

  if (showMetadataViews) {
    fieldSets.view.push(
      <SelectMetadataViewFieldWrapper
        objectType="assets"
        category="default"
        name="share.metadata_views"
        label="Assets metadata"
        {...fieldProps.metadata_views}
        isMulti
        canClear
        plainMultiValues
        key="asset_metadata"
      />,
    );
  }

  let expiresField;
  switch (fieldProps.expires.type) {
    case 'date':
      expiresField = (
        <SimpleDateFieldWrapper
          {...fieldProps.expires.fieldProps}
          required
          disabled={defaultSettings?.can_change_share_expiration_time === false}
          canClear={false}
          label="Share expiry"
          name="share.expires"
          isRangeMode={false}
          initialMonth={startOfMonth(Date.now())}
          key="expiry_date"
          hideAsterisk={Boolean(fieldProps.expires.fieldProps.value)}
        />
      );
      break;

    case 'days':
      expiresField = (
        <TextFieldWrapper
          {...fieldProps.expires.fieldProps}
          required
          disabled={defaultSettings?.can_change_share_expiration_time === false}
          canClear
          name="share.expires"
          label="Expires in days"
          key="expires"
        />
      );
  }

  return (
    <>
      <div>
        <TextFieldWrapper
          {...fieldProps.title}
          name="share.title"
          label="Custom title"
          key="title"
        />
        {!hideMessageField && (
          <TextArea
            {...fieldProps.message}
            name="share.message"
            label="Message"
            key="message"
          />
        )}
        {selectedItems && showVersionSelector && onSetSelectedVersions ? (
          <>
            <TooltipTrigger
              placement="top"
              tooltipText={
                !defaultSettings?.allow_view_versions
                  ? 'Sharing versions is disabled, please contact your administrator'
                  : ''
              }
            >
              <RadioButtonsWrapper
                {...fieldProps?.viewVersions}
                name="share.viewVersions"
                aria={{ label: 'Version selector' }}
                label="Asset versions"
                helpMessage="Add latest, all, or specific asset versions to the playlist"
                options={versionTypeOptions}
                disabled={!defaultSettings?.allow_view_versions}
                value={fieldProps?.viewVersions?.value ?? 'latest'}
              />
            </TooltipTrigger>
            {fieldProps?.viewVersions?.value === 'specify' ? (
              <VersionSelector
                selectedItems={selectedItems}
                className={styles.version_selector}
                selectedVersions={selectedVersions || {}}
                onSetSelectedVersions={onSetSelectedVersions}
              />
            ) : null}
          </>
        ) : null}
        {expiresField}
        <CheckboxGroupFieldWrapper
          label="Allow"
          labelWidth={fieldProps.allow.labelWidth}
          key="allow"
          options={[
            {
              label: 'Download',
              value: 'allow_download',
            },
            {
              label: 'Download Proxy',
              value: 'allow_download_proxy',
            },
            {
              label: 'Upload',
              value: 'allow_upload',
            },
          ]}
          controlClassName={styles.allow_field_checkbox_container}
          checkboxLabelClassName={styles.allow_field_checkbox_group_label}
          componentWrapper={(option, element: ReactElement) => {
            switch (option.value) {
              case 'allow_download':
                return (
                  <CheckboxFieldWrapper
                    {...fieldProps.allow_download}
                    disabled={
                      defaultSettings?.can_change_allow_download === false
                    }
                    name="share.allow_download"
                    key="allow_download"
                    label="Download"
                    labelClassName={classNames(
                      styles.checkbox_group_inner_labels,
                    )}
                    className={styles.allow_checkbox}
                  />
                );

              case 'allow_download_proxy':
                return (
                  <CheckboxFieldWrapper
                    {...fieldProps.allow_download_proxy}
                    disabled={
                      defaultSettings?.can_change_allow_download_proxy === false
                    }
                    name="share.allow_download_proxy"
                    key="allow_download_proxy"
                    label="Download Proxy"
                    labelClassName={styles.checkbox_group_inner_labels}
                    className={styles.allow_checkbox}
                  />
                );

              case 'allow_upload':
                return objectId && objectType === OBJECT_TYPE_COLLECTIONS ? (
                  <UploadSwitch
                    disabled={
                      defaultSettings?.can_change_allow_upload === false
                    }
                    fieldProps={fieldProps}
                    objectId={objectId}
                    labelClassName={classNames(
                      styles.checkbox_group_inner_labels,
                    )}
                    className={styles.allow_checkbox}
                  />
                ) : (
                  <TooltipTrigger
                    placement="right"
                    tooltipText="Share a collection to enable uploading"
                  >
                    <CheckboxFieldWrapper
                      value={false}
                      disabled={true}
                      label="Upload"
                      labelClassName={classNames(
                        styles.checkbox_group_inner_labels,
                      )}
                      className={styles.allow_checkbox}
                      labelWidth={fieldProps.allow_upload?.labelWidth}
                    />
                  </TooltipTrigger>
                );

              default:
                return element;
            }
          }}
        />
      </div>
      {hasShareMultiFieldRoles(['upload_storage_id'], roles) &&
      objectId &&
      objectType === OBJECT_TYPE_COLLECTIONS ? (
        <UploadSetting
          fieldProps={fieldProps}
          disabled={
            defaultSettings?.can_change_allow_upload === false ||
            !fieldProps.allow_upload?.value
          }
          objectId={objectId}
          key="upload_settings"
        />
      ) : null}
      {!hidePasswordField ? (
        <div key="password">
          <PasswordGenerator
            {...fieldProps.password}
            name="share.password"
            label="Set password"
            helpMessage={
              hasPassword ? 'The password has been already set' : undefined
            }
            displayCopyIcon
            displayGeneratorInField
            key="password"
          />
        </div>
      ) : null}
      {isPanel ? (
        advancedFields
      ) : (
        <Collapsible
          triggerClassName={styles.advanced_trigger}
          trigger="Advanced"
          onTriggerClick={onAdvancedClick}
          showContent={showAdvanced}
          uiKey="share-form-advanced"
          icon={showAdvanced ? ICONS.NavUp : ICONS.NavDown}
          triggerIconClassName={styles.advanced_collapsible_trigger_icon}
          dividerClassName={styles.advanced_trigger_divider}
          displayTriggerDivider
        >
          {advancedFields}
        </Collapsible>
      )}
    </>
  );
};

export default ShareSettings;
